/**
 * Where a student signs in.
 *
 * The card that stands in front of the assistant, and the strip that says who
 * is signed in once a student is through it. Both are pure builders - they read
 * the auth controller and return a node. Nothing here re-renders itself; the
 * panel that owns the node is subscribed to the controller and rebuilds when
 * the state moves, which keeps "who owns the DOM" with a single answer.
 *
 * TWO PLACES TO SIGN IN, ONE ACCOUNT. `AccountMenu` in the corner is the one a
 * student meets first and the one that is always there, because an account is
 * not an assistant key - it is the thing saved work will hang off. This card is
 * the fallback for anyone who walked past it, and it is here rather than
 * everywhere because the assistant is the one feature that cannot proceed
 * without knowing who is asking.
 *
 * The copy matters more than usual here. A sign-in is the one moment where a
 * piece of educational software asks a student for something instead of giving
 * them something, so it says plainly what it wants, what it does not take, and
 * that the guest door is a real door and not a downgrade.
 */

import { auth } from '../auth/Auth'
import { t } from '../i18n/i18n'
import { el } from './dom'
import { confirmDialog } from '../app/ui/confirm'

/**
 * Google's mark, inline. It is drawn rather than linked because the app has to
 * work on a classroom network that may not reach Google's CDN - and because a
 * "Continue with Google" button without the mark on it is a button students
 * hesitate over.
 */
export function googleMark(): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 48 48')
  svg.setAttribute('aria-hidden', 'true')
  svg.setAttribute('focusable', 'false')
  const paths: Array<[string, string]> = [
    [
      '#4285F4',
      'M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z',
    ],
    [
      '#34A853',
      'M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z',
    ],
    [
      '#FBBC05',
      'M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z',
    ],
    [
      '#EA4335',
      'M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z',
    ],
  ]
  for (const [fill, d] of paths) {
    const p = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    p.setAttribute('fill', fill)
    p.setAttribute('d', d)
    svg.appendChild(p)
  }
  return svg
}

/**
 * Ask before minting an anonymous account, then mint it. Shared with the corner
 * menu so the warning is worded and weighted the same in both places.
 */
export async function confirmGuest(): Promise<void> {
  const go = await confirmDialog({
    title: t('auth.ConfirmGuestTitle'),
    body: t('auth.ConfirmGuestBody'),
    okText: t('auth.Yes'),
    cancelText: t('auth.No'),
  })
  if (go) void auth.signInAsGuest()
}

/**
 * Ask before destroying one. Only the anonymous case is warned: signing out of
 * a Google account costs nothing, since the account and anything under it are
 * still there afterwards.
 */
export async function confirmSignOut(): Promise<void> {
  if (auth.account?.anonymous) {
    const go = await confirmDialog({
      title: t('auth.ConfirmSignOutTitle'),
      body: t('auth.ConfirmSignOutBody'),
      okText: t('auth.Yes'),
      cancelText: t('auth.No'),
    })
    if (!go) return
  }
  void auth.signOut()
}

/**
 * What a signed-out student sees where the transcript would be.
 *
 * `blurb` is the owning tool's one-liner, repeated here so the card says what
 * is behind the door rather than only that there is one.
 */
export function signInGate(blurb: string): HTMLElement {
  const busy = auth.pending || auth.phase === 'loading'

  const google = el('button', {
    class: 'btn primary auth-btn',
    disabled: busy,
    onclick: () => void auth.signInWithGoogle(),
  })
  google.appendChild(googleMark())
  google.appendChild(document.createTextNode(t('auth.Google')))

  // Warned exactly as the corner menu warns. Which door a student walked
  // through should not change what they were told the anonymous one costs.
  const guest = el('button', {
    class: 'btn auth-btn',
    text: t('auth.Guest'),
    disabled: busy,
    onclick: () => void confirmGuest(),
  })

  return el('div', { class: 'auth-gate' }, [
    el('div', { class: 'auth-card' }, [
      el('div', { class: 'auth-title', text: t('auth.Title') }),
      el('div', { class: 'auth-body', text: blurb }),
      el('div', { class: 'auth-body', text: t('auth.Why') }),

      el('div', { class: 'auth-actions' }, [google, guest]),

      el('div', {
        class: 'auth-note',
        text: auth.phase === 'loading' ? t('auth.Checking') : t('auth.GuestNote'),
      }),

      auth.error
        ? el('div', { class: 'auth-error' }, [
            el('div', { text: auth.error }),
            el('button', {
              class: 'btn',
              text: t('auth.Retry'),
              style: 'margin-top:8px',
              onclick: () => void auth.signInWithGoogle(),
            }),
          ])
        : null,
    ]),
  ])
}

